import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { StandardEvents } from '@shopify/events';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { formatMoney } from '@theme/money-formatting';

/** Display-only pricing; Shopify's variant response is the only price source. */
class EvPurchaseSummary extends Component {
  #controller;
  #generation = 0;
  #pendingVariant = false;
  #scope;
  #frame;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#controller = new AbortController();
    this.#scope = this.closest('product-component, .shopify-section, dialog');
    const { signal } = this.#controller;
    this.#scope?.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#onQuantity, { signal });
    this.#scope?.addEventListener(StandardEvents.productSelect, this.#onVariant, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCart, { signal });
    this.#scheduleUpdate();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    cancelAnimationFrame(this.#frame);
    this.#generation++;
    this.#pendingVariant = false;
    super.disconnectedCallback();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#scheduleUpdate();
  }

  #scheduleUpdate() {
    const generation = this.#generation;
    queueMicrotask(() => {
      if (!this.isConnected || generation !== this.#generation || this.#pendingVariant) return;
      cancelAnimationFrame(this.#frame);
      // Native form listeners can morph the CTA and quantity constraints after
      // the same product-select promise settles. Read their finished DOM next frame.
      this.#frame = requestAnimationFrame(() => {
        this.#frame = undefined;
        if (this.isConnected && generation === this.#generation && !this.#pendingVariant) this.#update();
      });
    });
  }

  #quantityInput() {
    return this.#scope?.querySelector('product-form-component quantity-selector-component input[name="quantity"]');
  }

  #onQuantity = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card, cart-items-component')) return;
    if (event.target.closest('product-form-component') !== this.#quantityInput()?.closest('product-form-component')) return;
    this.#scheduleUpdate();
  };

  #onCart = async (event) => {
    const generation = this.#generation;
    try {
      await event.promise;
      if (this.isConnected && generation === this.#generation) this.#scheduleUpdate();
    } catch (error) {
      if (error?.name !== 'AbortError') console.warn('[ev-purchase-summary] Cart update failed:', error);
    }
  };

  #onVariant = async (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card, quick-add-dialog')) return;
    const generation = ++this.#generation;
    this.#pendingVariant = true;
    this.hidden = true;
    try {
      const { detail } = await event.promise;
      if (!this.isConnected || generation !== this.#generation) return;
      const next = Array.from(detail?.html?.querySelectorAll('ev-purchase-summary') ?? [])
        .find((element) => element.dataset.blockId === this.dataset.blockId);
      if (!next) return;
      // Price and variant IDs live on the host; the theme defaults to children-only morphs.
      morph(this, next, { ...MORPH_OPTIONS, childrenOnly: false });
      this.#pendingVariant = false;
      this.#scheduleUpdate();
    } catch (error) {
      if (!this.isConnected || generation !== this.#generation) return;
      // Keep stale offer values hidden if the selected variant could not resolve.
      if (error?.name !== 'AbortError') console.warn('[ev-purchase-summary] Variant update failed:', error);
    }
  };

  #update() {
    this.hidden = this.dataset.offer !== 'true';
    const price = Number(this.dataset.price);
    const compareAt = Number(this.dataset.compareAt);
    const input = this.#quantityInput();
    const quantity = input ? Number(input.value) : Number(this.dataset.minQuantity);
    if (!this.dataset.variantId || this.dataset.price === '' || !Number.isFinite(price) || !Number.isInteger(quantity) || quantity < 1) {
      this.hidden = true;
      return;
    }
    const cartQuantity = Number(input?.dataset.cartQuantity || 0);
    const tierQuantity = quantity + (Number.isFinite(cartQuantity) ? cartQuantity : 0);
    const tiers = Array.from(this.querySelectorAll('[data-tier-min]'));
    let unitPrice = price;
    let activeTier;
    for (const tier of tiers) {
      const minimum = Number(tier.dataset.tierMin);
      const tierPrice = Number(tier.dataset.tierPrice);
      if (tierQuantity >= minimum && Number.isFinite(tierPrice) && tierPrice >= 0) {
        unitPrice = tierPrice;
        activeTier = tier;
      }
    }
    for (const tier of tiers) tier.toggleAttribute('data-active', tier === activeTier);
    const money = (value) => formatMoney(value, this.dataset.moneyFormat, this.dataset.currency);
    const total = money(unitPrice * quantity);
    for (const ctaPrice of this.#scope?.querySelectorAll('.buy-buttons-block [data-ev-cta-price]') ?? []) {
      ctaPrice.textContent = total;
    }
    this.querySelector('[data-unit-price]').textContent = money(unitPrice);
    this.querySelector('[data-total]').textContent = total;
    this.querySelector('[data-summary-quantity]').textContent = `(${quantity})`;
    const originalUnitPrice = Math.max(price, compareAt);
    const savings = originalUnitPrice > unitPrice ? originalUnitPrice - unitPrice : 0;
    this.querySelector('[data-regular-price]').hidden = savings <= 0;
    this.querySelector('[data-saving]').hidden = savings <= 0;
    if (savings > 0) {
      this.querySelector('[data-saving-amount]').textContent = money(savings);
      this.querySelector('[data-saving-percent]').textContent = String(Math.floor(savings * 100 / originalUnitPrice));
    }
  }
}

if (!customElements.get('ev-purchase-summary')) customElements.define('ev-purchase-summary', EvPurchaseSummary);
